import { isAfter } from 'date-fns/isAfter';
import { isSameDay } from 'date-fns/isSameDay';

import { t } from '@grafana/i18n';
import { isEmail } from 'app/extensions/utils/validators';

import { type ValidationResult } from '../../shared/types';
import { type FormRequiredFields, type Report, type ReportFormV2, type ReportURLItem, StepKey } from '../../types';
import { reportSteps } from '../index';

const arrayFieldIsValid = (field: Report[keyof Report]) => {
  if (Array.isArray(field)) {
    return field?.length;
  }

  return true;
};

export const getValidationResults = (report: Report) => {
  const requiredFields: FormRequiredFields = [
    { step: StepKey.SelectDashboard, fields: ['dashboards'] },
    { step: StepKey.FormatReport, fields: ['formats'] },
    { step: StepKey.Share, fields: ['name', 'recipients'] },
  ];
  const hasMissingDashboard = !report.dashboards?.[0]?.dashboard?.uid;
  return reportSteps.reduce(
    (sum, curr) => {
      sum[curr.id] = { valid: true };
      if (curr.id === StepKey.SelectDashboard) {
        sum[StepKey.SelectDashboard].valid = !hasMissingDashboard;
        return sum;
      } else {
        const fields = requiredFields.find((field) => field.step === curr.id)?.fields;
        if (fields) {
          sum[curr.id].valid = fields.every((field: keyof Report) => report[field] && arrayFieldIsValid(report[field]));
        }
        return sum;
      }
    },
    {} as Record<StepKey, ValidationResult>
  );
};

export const getMissingFields = (report: Report, id?: StepKey) => {
  const results = getValidationResults(report);
  if (id) {
    return !results[id].valid;
  }
  return Object.values(results).some((field) => !field.valid);
};

/** A url row only counts as filled in once both its url and title have content. */
const isFilledInUrl = (url: ReportURLItem) => url.url.trim() !== '' && url.title.trim() !== '';

export const hasAnyFilledInUrl = (urls?: ReportURLItem[]) => !!urls?.some(isFilledInUrl);

/** Whether the report has at least one picked dashboard or one filled-in url page. */
export const hasReportContent = (form?: Partial<Pick<ReportFormV2, 'dashboards' | 'urls'>>) =>
  !!form?.dashboards?.some((d) => !!d.uid) || hasAnyFilledInUrl(form?.urls);

export const formSchemaValidationRules = (form?: Partial<ReportFormV2>) => ({
  title: {
    required: t('share-report.report-name.required', 'Report name is required'),
  },
  dashboards: {
    uid: {
      // Deliberately narrower than hasReportContent: a url elsewhere exempts an empty
      // dashboard slot (a report can be url-only), but a sibling dashboard row does not —
      // a row the user added and left blank should still be filled in or removed, not
      // silently excused because some other row already has a uid.
      validate: (val: string | undefined) => {
        if (val) {
          return true;
        }
        if (hasAnyFilledInUrl(form?.urls)) {
          return true;
        }
        return t('share-report.dashboard.dashboard-required', 'Dashboard is required');
      },
    },
  },
  urls: {
    title: {
      validate: (val: string) => val.trim().length > 0 || t('share-report.urls.title-required', 'Title is required'),
    },
    url: {
      validate: (val: string) => {
        const trimmed = val.trim();
        const invalid = t('share-report.urls.url-must-be-relative-path', 'URL must be a relative path starting with /');
        if (trimmed.length === 0) {
          return t('share-report.urls.url-required', 'URL is required');
        }
        if (!trimmed.startsWith('/')) {
          return invalid;
        }
        // Mirror the backend validators (pkg/report/service/validators.go's ValidateURLs and
        // pkg/apps/reporting/pkg/app/validators.go's validateReportURL): decode once and reject
        // any literal ".." left in the result, so a form that looks valid here doesn't fail the
        // create/update request with a generic API error instead of this inline field message.
        let decoded: string;
        try {
          decoded = decodeURIComponent(trimmed);
        } catch {
          return t('share-report.urls.url-invalid-escape', 'URL contains invalid escape sequence');
        }
        if (decoded.includes('..')) {
          return t('share-report.urls.url-path-traversal', 'URL must not contain path traversal sequences');
        }
        // Resolve the way a browser would when following this path, so a scheme/host trick
        // hidden in the string (protocol-relative `//`, backslash or control-character host
        // confusion, etc.) is caught by an actual origin mismatch instead of a growing list of
        // string checks — see handleRedirectTo.ts / locationUtil.processRedirectUri for the
        // same same-origin-via-URL pattern used elsewhere in Grafana for redirect validation.
        let resolved: URL;
        try {
          resolved = new URL(trimmed, window.location.origin);
        } catch {
          return invalid;
        }
        return resolved.origin === window.location.origin || invalid;
      },
    },
  },
  replyTo: {
    validate: (val: string | undefined) => {
      if (!val) {
        return true;
      }
      return isEmail(val) || t('share-report.email-configuration.invalid-email', 'Invalid email');
    },
  },
  schedule: {
    startDate: {
      required: t('share-report.schedule.start-date-required', 'Start date is required'),
    },
    endDate: {
      validate: (val: Date | string | undefined) => {
        if (!val || !form?.schedule?.startDate) {
          return true;
        }

        const isEndDateValid =
          form?.schedule?.startDate &&
          (isSameDay(val, form.schedule.startDate) || isAfter(val, form.schedule.startDate));

        return (
          isEndDateValid ||
          t('share-report.schedule.end-date-invalid', 'End date must be greater or equal to start date')
        );
      },
    },
    intervalAmount: {
      required: t('share-report.schedule.interval-amount-required', 'Interval amount is required'),
      min: {
        value: 2,
        message: t('share-report.schedule.interval-amount-min', 'Interval amount must be at least 2'),
      },
    },
  },
  recipients: {
    required: t('share-report.recipients.recipients-required', 'Recipients are required'),
  },
});
